import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Avatar,
  Rating,
  IconButton,
} from '@mui/material';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import HubIcon from '@mui/icons-material/Hub';
import DescriptionIcon from '@mui/icons-material/Description';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import {
  brand,
  loanImages,
  unifiedHeroMinHeightHome,
  COMPANY_PHONE,
  COMPANY_PHONE_TEL,
  COMPANY_EMAIL_DOCUMENT,
} from '../brandTheme';

const NAV_OFFSET = 72;
const CARD_RADIUS = '20px';
const EASE_OUT = [0.22, 1, 0.36, 1];

const heroTextParent = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.14, delayChildren: 0.12 },
  },
};

const heroFadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: EASE_OUT },
  },
};

const trustParent = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.35 },
  },
};

const trustItem = {
  hidden: { opacity: 0, y: 18, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: EASE_OUT },
  },
};

/** Credibility row inside hero — no long accreditation list */
const HERO_TRUST = [
  { icon: <VerifiedUserIcon sx={{ fontSize: 26 }} />, label: 'Guided applications' },
  { icon: <HubIcon sx={{ fontSize: 26 }} />, label: 'Multi-lender compare' },
  { icon: <DescriptionIcon sx={{ fontSize: 26 }} />, label: 'Document clarity' },
  { icon: <SupportAgentIcon sx={{ fontSize: 26 }} />, label: 'Thane West team' },
];

const HOW_COMPACT = [
  {
    title: 'Apply',
    body: 'One flow for your profile and loan need.',
    color: brand.stepAccent1,
  },
  {
    title: 'Verify',
    body: 'We align paperwork with lender formats.',
    color: brand.stepAccent2,
  },
  {
    title: 'Disburse',
    body: 'Track offers through sanction to payout.',
    color: brand.stepAccent3,
  },
];

const SOLUTIONS = [
  {
    title: 'Lower rates',
    body: 'We benchmark offers across our partner network so you see clear, comparable terms.',
    icon: <TrendingDownIcon sx={{ fontSize: 44 }} />,
    bg: brand.solutionCard1,
  },
  {
    title: 'Trusted brand',
    body: 'Document-led processes aligned with regulated institutions—full transparency, no shortcuts.',
    icon: <EmojiEventsIcon sx={{ fontSize: 44 }} />,
    bg: brand.solutionCard2,
  },
  {
    title: 'Speed',
    body: 'Coordinators track status with banks and NBFCs to cut idle time on your file.',
    icon: <AccessTimeIcon sx={{ fontSize: 44 }} />,
    bg: brand.solutionCard3,
  },
];

const STEPS_GET_STARTED = [
  {
    n: 1,
    title: 'Consultation',
    body: 'Discuss goals, eligibility, and the right product mix.',
    img: loanImages.stepConsult,
    footerBg: brand.stepAccent1,
  },
  {
    n: 2,
    title: 'Documentation',
    body: 'Submit KYC and income proofs—we handle lender formatting.',
    img: loanImages.stepDocs,
    footerBg: brand.stepAccent2,
  },
  {
    n: 3,
    title: 'Closure',
    body: 'Sign, insure if needed, and receive disbursal or policy confirmation.',
    img: loanImages.stepClose,
    footerBg: brand.stepAccent3,
  },
];

const TESTIMONIALS = [
  {
    name: 'Rahul Sharma',
    role: 'Business owner',
    text: 'I needed urgent funds for my business expansion. Om Sai Enterprises helped me get a loan within 48 hours. Excellent service!',
    rating: 5,
    avatarBg: '#4f46e5',
  },
  {
    name: 'Priya Mehta',
    role: 'Salaried professional',
    text: 'Clear explanations at every step—no jargon. The team compared two bank offers and we picked the better EMI structure.',
    rating: 5,
    avatarBg: '#0f766e',
  },
  {
    name: 'Amit Desai',
    role: 'Self-employed',
    text: 'Documentation felt overwhelming until they broke it into a checklist. Disbursal timeline matched what they committed.',
    rating: 5,
    avatarBg: '#b45309',
  },
  {
    name: 'Sneha Kulkarni',
    role: 'Home buyer',
    text: 'From pre-approval to insurance add-on, one coordinator kept everything moving. Would recommend for home loans in Thane.',
    rating: 5,
    avatarBg: '#7c3aed',
  },
];

export default function Hero() {
  const [activeReview, setActiveReview] = useState(0);
  const t = TESTIMONIALS[activeReview];

  return (
    <Box sx={{ overflowX: 'hidden' }} className="component-box component-box--flush">
      {/* Hero — layered bg motion + staggered copy + trust row */}
      <Box
        id="home"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          mt: `-${NAV_OFFSET}px`,
          pt: `${NAV_OFFSET + 36}px`,
          pb: 0,
          px: 0,
          minHeight: unifiedHeroMinHeightHome,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <motion.div
          initial={{ scale: 1.12, opacity: 0.75 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.45, ease: EASE_OUT }}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            backgroundImage: `url(${loanImages.hero})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            transformOrigin: 'center center',
          }}
        />
        <motion.div
          aria-hidden
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.05 }}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
            background: `linear-gradient(115deg, rgba(15, 23, 42, 0.92) 0%, rgba(49, 46, 129, 0.55) 42%, rgba(15, 118, 110, 0.35) 100%)`,
          }}
        />
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 2,
            width: '100%',
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            py: { xs: 9, md: 14 },
          }}
        >
          <Grid container>
            <Grid item xs={12} md={7} lg={6}>
              <motion.div variants={heroTextParent} initial="hidden" animate="visible">
                <motion.div variants={heroFadeUp}>
                  <Typography
                    component="h1"
                    sx={{
                      fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
                      fontWeight: 800,
                      fontSize: { xs: '2.35rem', sm: '2.95rem', md: '3.45rem' },
                      lineHeight: 1.1,
                      color: '#fff',
                      letterSpacing: '-0.03em',
                      mb: 2.5,
                      textShadow: '0 2px 28px rgba(0,0,0,0.35)',
                    }}
                  >
                    The right loan for your financial freedom
                  </Typography>
                </motion.div>
                <motion.div variants={heroFadeUp}>
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: { xs: '1.125rem', md: '1.2rem' },
                      lineHeight: 1.75,
                      maxWidth: 540,
                      mb: 4,
                      fontWeight: 500,
                    }}
                  >
                    Personal loans, business capital, home finance, and insurance—clear guidance from Om Sai Enterprises,
                    Thane West.
                  </Typography>
                </motion.div>
                <motion.div
                  variants={heroFadeUp}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  style={{ display: 'inline-block' }}
                >
                  <Button
                    component="a"
                    href="#/contact"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    variant="contained"
                    size="large"
                    sx={{
                      borderRadius: '999px',
                      px: 4.5,
                      py: 1.65,
                      fontWeight: 800,
                  fontSize: '0.875rem',
                  letterSpacing: '0.08em',
                  bgcolor: '#fbbf24',
                      color: '#422006',
                      boxShadow: '0 8px 28px rgba(0,0,0,0.28)',
                      '&:hover': { bgcolor: '#fcd34d', boxShadow: '0 10px 32px rgba(0,0,0,0.3)' },
                    }}
                  >
                    GET YOUR LOAN NOW
                  </Button>
                </motion.div>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.55, ease: EASE_OUT }}
          sx={{
            position: 'relative',
            zIndex: 2,
            borderTop: '1px solid rgba(255,255,255,0.14)',
            background: 'linear-gradient(180deg, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.72) 100%)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            py: { xs: 2.25, md: 2.75 },
          }}
        >
          <Container maxWidth="lg">
            <Stack
              component={motion.div}
              variants={trustParent}
              initial="hidden"
              animate="visible"
              direction="row"
              flexWrap="wrap"
              useFlexGap
              spacing={{ xs: 2, md: 3 }}
              justifyContent={{ xs: 'center', md: 'space-between' }}
              alignItems="center"
            >
              {HERO_TRUST.map((item) => (
                <Stack
                  key={item.label}
                  component={motion.div}
                  variants={trustItem}
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  whileHover={{ y: -2, transition: { duration: 0.2 } }}
                  sx={{ color: 'rgba(255,255,255,0.92)', minWidth: { xs: '42%', sm: 'auto' } }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '12px',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(255,255,255,0.14)',
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', letterSpacing: '0.02em' }}>{item.label}</Typography>
                </Stack>
              ))}
            </Stack>
          </Container>
        </Box>
      </Box>

      {/* How our loan works — compact band */}
      <Box sx={{ py: { xs: 4, md: 5 }, bgcolor: brand.surfaceWarm, borderBottom: `1px solid ${brand.border}` }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 3 }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: brand.forest,
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.35rem', md: '1.5rem' },
              }}
            >
              How our loan works
            </Typography>
            <Button
              component="a"
              href="#/contact"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              variant="contained"
              size="small"
              sx={{
                borderRadius: '999px',
                px: 3,
                py: 1,
                fontWeight: 800,
                fontSize: '0.65rem',
                letterSpacing: '0.1em',
                bgcolor: brand.accent,
                color: '#fff',
                boxShadow: 'none',
                alignSelf: { xs: 'flex-start', sm: 'center' },
                '&:hover': { bgcolor: brand.accentDark, boxShadow: 'none' },
              }}
            >
              CONTACT US
            </Button>
          </Stack>
          <Grid container spacing={2}>
            {HOW_COMPACT.map((step) => (
              <Grid item xs={12} sm={4} key={step.title}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.25,
                    borderRadius: 2.5,
                    border: `1px solid ${brand.border}`,
                    bgcolor: '#fff',
                    height: '100%',
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: `${step.color}18`,
                      color: step.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: '0.9rem',
                      flexShrink: 0,
                    }}
                  >
                    {step.title[0]}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, color: brand.forest, mb: 0.5, fontSize: '0.95rem' }}>
                      {step.title}
                    </Typography>
                    <Typography sx={{ color: brand.muted, fontSize: '0.8125rem', lineHeight: 1.55 }}>{step.body}</Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Three solution cards */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: '#fff', borderBottom: `1px solid ${brand.border}` }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: 800,
              color: brand.indigo,
              mb: 2,
              letterSpacing: '-0.02em',
              fontSize: { xs: '1.75rem', md: '2.1rem' },
            }}
          >
            Count on our professional solutions
          </Typography>
          <Typography
            align="center"
            sx={{ color: brand.muted, maxWidth: 620, mx: 'auto', mb: { xs: 5, md: 6 }, lineHeight: 1.8, fontSize: '0.9375rem' }}
          >
            Institutional discipline with the accessibility of a local advisor—built for families and businesses across
            Thane and Mumbai.
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {SOLUTIONS.map((card) => (
              <Grid item xs={12} sm={6} md={4} key={card.title} sx={{ display: 'flex' }}>
                <Paper
                  elevation={0}
                  sx={{
                    width: '100%',
                    p: { xs: 3.5, md: 4.5 },
                    borderRadius: CARD_RADIUS,
                    bgcolor: card.bg,
                    color: '#fff',
                    border: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    minHeight: { xs: 280, md: 300 },
                  }}
                >
                  <Box sx={{ mb: 2.5, opacity: 0.98, display: 'flex', justifyContent: 'center' }}>{card.icon}</Box>
                  <Typography sx={{ fontWeight: 800, mb: 2, fontSize: '1.2rem', letterSpacing: '-0.01em' }}>
                    {card.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.9375rem', lineHeight: 1.75, opacity: 0.95, maxWidth: 280, mx: 'auto' }}>
                    {card.body}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials — click avatars to switch */}
      <Box
        sx={{
          py: { xs: 7, md: 9 },
          backgroundImage: `linear-gradient(115deg, rgba(30, 27, 75, 0.94) 0%, rgba(30, 41, 59, 0.9) 55%, rgba(15, 23, 42, 0.88) 100%), url(${loanImages.testimonialBand})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
            <Box sx={{ flex: { md: '0 0 240px' } }}>
              <FormatQuoteIcon sx={{ fontSize: 56, color: 'rgba(255,255,255,0.35)', mb: 1, display: 'block' }} />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  color: '#fff',
                  letterSpacing: '-0.02em',
                  fontSize: { xs: '1.65rem', md: '2rem' },
                  mb: 2,
                }}
              >
                What they say
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8125rem', mb: 2, display: { xs: 'none', md: 'block' } }}>
                Tap a photo to read another review.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {TESTIMONIALS.map((person, i) => (
                  <IconButton
                    key={person.name}
                    onClick={() => setActiveReview(i)}
                    aria-label={`Show review from ${person.name}`}
                    sx={{
                      p: 0.5,
                      border: activeReview === i ? '3px solid #fbbf24' : '3px solid transparent',
                      borderRadius: '50%',
                    }}
                  >
                    <Avatar sx={{ width: 48, height: 48, bgcolor: person.avatarBg, fontWeight: 800 }}>
                      {person.name[0]}
                    </Avatar>
                  </IconButton>
                ))}
              </Stack>
            </Box>
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: { xs: 3, md: 4 },
                borderRadius: CARD_RADIUS,
                bgcolor: '#fff',
                border: 'none',
                boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
                minHeight: 220,
                transition: 'opacity 0.2s ease',
              }}
              key={activeReview}
            >
              <Typography sx={{ color: brand.indigo, fontWeight: 700, fontSize: '1.02rem', mb: 2, lineHeight: 1.7 }}>
                &ldquo;{t.text}&rdquo;
              </Typography>
              <Rating
                value={t.rating}
                readOnly
                sx={{ mb: 2.5, '& .MuiRating-iconFilled': { color: brand.accent } }}
              />
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: t.avatarBg, width: 48, height: 48, fontWeight: 800 }}>{t.name[0]}</Avatar>
                <Box>
                  <Typography fontWeight={800} color={brand.forest} fontSize="0.95rem">
                    {t.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontSize="0.8125rem">
                    {t.role}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Box>

      {/* Mid CTA */}
      <Box sx={{ bgcolor: brand.slate, py: { xs: 4, md: 5 } }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Box sx={{ maxWidth: 560 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#fff', mb: 1, letterSpacing: '-0.02em', fontSize: '1.15rem' }}>
                Gracefully streamline your finances
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.65, fontSize: '0.875rem' }}>
                One conversation to align loans, insurance, and documentation—with a single accountable team.
              </Typography>
            </Box>
            <Button
              component="a"
              href="#/contact"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              variant="contained"
              sx={{
                borderRadius: '999px',
                px: 3.5,
                py: 1.35,
                fontWeight: 800,
                fontSize: '0.65rem',
                letterSpacing: '0.12em',
                alignSelf: { xs: 'flex-start', md: 'center' },
                bgcolor: '#fbbf24',
                color: '#422006',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#fcd34d', boxShadow: 'none' },
              }}
            >
              APPLY YOUR LOAN NOW
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Getting started — three in one row from sm up */}
      <Box sx={{ py: { xs: 7, md: 9 }, bgcolor: brand.surfaceWarm }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{ fontWeight: 800, color: brand.forest, mb: 1.5, letterSpacing: '-0.02em', fontSize: { xs: '1.65rem', md: '2rem' } }}
          >
            Getting started
          </Typography>
          <Typography
            align="center"
            sx={{ color: brand.muted, mb: { xs: 4, md: 5 }, maxWidth: 520, mx: 'auto', lineHeight: 1.65, fontSize: '0.875rem' }}
          >
            Three stages from first call to disbursal.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 3, sm: 2, md: 3 },
              alignItems: 'stretch',
              justifyContent: 'center',
            }}
          >
            {STEPS_GET_STARTED.map((step) => (
              <Box
                key={step.n}
                sx={{
                  flex: { sm: '1 1 0' },
                  minWidth: 0,
                  maxWidth: { xs: 420, sm: 'none' },
                  mx: { xs: 'auto', sm: 0 },
                  textAlign: 'center',
                }}
              >
                <Box
                  component="img"
                  src={step.img}
                  alt=""
                  sx={{
                    width: '100%',
                    height: { xs: 200, sm: 200, md: 220 },
                    objectFit: 'cover',
                    borderRadius: `${CARD_RADIUS} ${CARD_RADIUS} 0 0`,
                    display: 'block',
                  }}
                />
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: step.footerBg,
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '1.2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mt: -3.5,
                    position: 'relative',
                    zIndex: 1,
                    border: '4px solid #fff',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  }}
                >
                  {step.n}
                </Box>
                <Box
                  sx={{
                    bgcolor: step.footerBg,
                    color: '#fff',
                    pt: 4.5,
                    pb: 3,
                    px: 2.5,
                    borderRadius: `0 0 ${CARD_RADIUS} ${CARD_RADIUS}`,
                    mt: -2,
                    minHeight: 150,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography fontWeight={800} sx={{ mb: 1, fontSize: '1rem' }}>
                    {step.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.65, opacity: 0.95 }}>{step.body}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Box sx={{ py: 3.5, bgcolor: '#fff', borderTop: `1px solid ${brand.border}` }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography sx={{ color: brand.muted, mb: 0.5, fontSize: '0.875rem' }}>
            <strong style={{ color: brand.forest }}>{COMPANY_PHONE}</strong>
            {' · '}
            <Box component="a" href={`mailto:${COMPANY_EMAIL_DOCUMENT}`} sx={{ color: brand.primary, fontWeight: 700 }}>
              {COMPANY_EMAIL_DOCUMENT}
            </Box>
            {' · '}
            <Box component="a" href={COMPANY_PHONE_TEL} sx={{ color: brand.primary, fontWeight: 700 }}>
              Call
            </Box>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
